import Radio from "backbone.radio";
import { useEffect, Fragment, Children } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Route as ReactRouterRoute,
    Switch as ReactRouterSwitch,
    Redirect,
    useLocation,
    useRouteMatch,
    useParams as ReactRouterUseParams
} from "react-router-dom";
import { store } from "common/store";
import { constant, partial, partialRight, mapValues } from "lodash-es";
import { captureBreadcrumb } from "extensions/sentry";
import { withSentryRouting } from "@sentry/react";

import {
    PARSE_COMPARE_DATES_URL,
    PARSE_DATES_URL,
    PARSE_DATETYPE_URL,
    PARSE_CURRENCY_URL,
    SET_MERCHANT_REF,
    SET_BU_REF,
    // REQUESTS_SET_ACTIVE_PRODUCT,
    ADD_TO_HISTORY_STACK
} from "common/actions/common";
import { RETURN_TO_CURRENT_URL_AFTER_LOGIN } from "common/actions/authentication";

import { pazienHistory } from "common/components/pazienHistory";

import { unsavedChangesHook } from "common/routers/unsavedChangesHook";
import { useAuth } from "common/hooks/useAuth";

/**
 * Set of all available stores that querystring processor can use
 * Class property since it's shared between instances and easier to mock and test
 */
const queryStringsStores = {
    dates(dates = "") {
        PARSE_DATES_URL({ dates });

        return true;
    },

    datetype(datetype = "") {
        PARSE_DATETYPE_URL({ datetype });

        return true;
    },

    currency(currency = "") {
        PARSE_CURRENCY_URL({ currency });

        return true;
    },

    merchant(merchant_ref = "") {
        store.dispatch(SET_MERCHANT_REF(merchant_ref, false));

        // Maybe not the best place to have this logic, but I cannot think now about any other solution
        return store.getState().config.deniedAccessStatusCode === 0;
    },

    bu(bu_ref = "") {
        store.dispatch(SET_BU_REF(bu_ref));

        return true;
    },

    compare(dates = "") {
        PARSE_COMPARE_DATES_URL({ dates });

        return true;
    }
};

// Create Custom Sentry Route component. https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
export const SentryRoute = withSentryRouting(ReactRouterRoute);

/*
  The following 2 Routes provide additional functionality to react-router's Routes by rendering a nested pair of routes for every route in the application.
  Because they both have the same `path` parameter, both routes will trigger when navigation occurs (unless the first route decides to redirect).
*/

/*
  This type of Route will render only when the user is NOT yet authenticated.
  If the user _is_ authenticated, they will be redirected to the start page instead.
 */
export function PreAuthRoute({ children, component, render, ...rest }) {
    const { isAuthenticated } = useAuth();
    return (
        <ReactRouterRoute
            {...rest}
            render={({ match, location, history }) => {
                // The application assumes that the only unauthenticated routes are sign-in related, so if the user is already signed-in, don't bother rendering those routes.
                if (isAuthenticated()) {
                    const startPage = store.getState().user_merchant_settings.start_page || store.getState().config.defaultRouteMerchant;
                    return <Redirect to={startPage}/>;
                }
    
                // The following is the default way react-router Routes are used.
                // eslint-disable-next-line
                return <SentryRoute component={component} render={render} {...rest}>{children}</SentryRoute>;
            }}
        />
    );
}

/*
  React-router does not provide a default route authentication feature.
  This type of Route will render only when the user IS authenticated.
  If the user is not authenticated, they will be redirected to the login page.
*/
export function AuthRoute({
    children,
    component,
    render,
    authenticated = constant(true),
    ...rest
}) {
    const { isAuthenticated } = useAuth();
    const dispatch = useDispatch();
    const prerequisites = useSelector((state) => state.common.user.prerequisites);
    const user_id = useSelector((state) => state.common.user._id);
    
    /**
     * Process each parsed querystring to store it in the right place or nullify existing value
     * @param {Object} querystrings
     */
    const processQueryStrings = (querystrings) => {
        // Return earlier if user is not logged in
        let result = true;
        if (!isAuthenticated()) {
            return result;
        }
        
        // eslint-disable-next-line no-restricted-syntax
        for (const param in queryStringsStores) {
            if (!queryStringsStores[param](querystrings[param])) {
                result = false;
            }
        }
        
        return result;
    };
    
    /**
     * Ruby on Rails-like beforeRoute.
     * Trigger this function before navigating to ANY route.
     * If it returns false than the route won't be triggered
     * @returns {Boolean}
     */
    const beforeRoute = () => {
        // TODO MATT: What do routes need a name for? probably nothing.
        ADD_TO_HISTORY_STACK({
            name: pazienHistory.getCurrentFragmentProduct(),
            // TODO MATT: remove if allowed. Just use history directly wtf it /is/ a stack.
            fragment: pazienHistory.getCurrentFragment()
        });
        
        // Check for unsaved changes
        return unsavedChangesHook();
    };
    
    return (
        <ReactRouterRoute
            {...rest}
            render={({ match, history }) => {
                // Automatically update Redux with parameters from the search string.
                // Must be done whether or not the user is authenticated.
                const queryStringsParsingResult = pazienHistory.parseQueryString();
                const processQueryStringResult = processQueryStrings(queryStringsParsingResult);
                
                // If the user is not yet authenticated, remember the location they were trying to access and make them sign in.
                if (!isAuthenticated()) {
                    // Store original destination as default navigate-after page
                    captureBreadcrumb({
                        message: "User is not authenticated. Storing attempted URL",
                        category: "router",
                        data: {
                            url: pazienHistory.getCurrentFragment()
                        }
                    });
                    
                    dispatch(RETURN_TO_CURRENT_URL_AFTER_LOGIN());
                    
                    return <Redirect to={{ pathname: "/merchant/login", state: { from: pazienHistory.getCurrentFragment() } }} />;
                }

                // TODO: move this user init somewhere else and refactor without Radio
                if (!user_id) {
                    // Instantiate user. This will also kick the process of fetching prerequisites
                    Radio.channel("main").request("get:user");
                    return null;
                }
                
                // The user is logged in and their prerequisites are fetched.
                if (prerequisites === "fetched") {
                    // Failing per-route `authenticated` callback implies the user is not allowed to access the route.
                    if (!authenticated(store.getState())) {
                        return <Redirect to={{ pathname: "/merchant/insufficient_privileges", state: { from: pazienHistory.getCurrentFragment() } }} />;
                    }

                    if (beforeRoute()) {
                        // TODO MATT: Not yet..
                        /* afterRoute(location); */

                        // eslint-disable-next-line
                        return processQueryStringResult && <SentryRoute component={component} render={render} {...rest}>{children}</SentryRoute>;
                    }
                }
                // User indicated they do NOT want to navigate away from their current page.
                return null;
            }}
        />
    );
}

// Convenience component to call a Backbone controller method when the route matches.
// Deprecated. Don't write any more Backbone controllers and don't use this component.
export function ControllerWrapper({ controller, method }) {
    const match = useRouteMatch();
    const location = useLocation();
    const queryParams = useQueryParameters();
    const params = useParams();

    useEffect(() => {
        if (match) {
            // Backbone controller methods expect ControllerOptions (always empty object) as first argument, and parsed query string as last argument.
            const boundMethod = partial(partialRight(controller[method].bind(controller), queryParams), {});

            if (location.pathname.includes("reconciliation")) {
                // recon2 routes specify this format.
                boundMethod(params.options, params.qualifiers);
            }
            // There is only 1 parameter
            boundMethod(...Object.values(params));
        }
    });

    return null;
}

// Override the default react router behavior to make sure parameters are decoded.
export const useParams = () => {
    const rawParameters = ReactRouterUseParams();
    return mapValues(rawParameters, (v) => (typeof v === "string" ? window.decodeURIComponent(v) : v));
};

// A custom hook that builds on useLocation to parse the query string for you.
export const useQueryParameters = () => mapValues(pazienHistory.parseQueryString(), (v) => (typeof v === "string" ? window.decodeURIComponent(v) : v));

// The default react router Switch does not support react Fragments as children, which is very inconvenient.
export function Switch({ children }) {
    const noFragments = [];
    const flattenChildren = (cs) => Children.forEach(cs, (child) => {
        if (child.type === Fragment) {
            flattenChildren(child.props.children);
        } else {
            noFragments.push(child);
        }
    });
    flattenChildren(children);

    return <ReactRouterSwitch>{noFragments}</ReactRouterSwitch>;
}

// We should default to using the extensions for react-router features, instead of importing react-router directly.
export * from "react-router-dom";
